import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../api/client';

export function useFavorites(query: Record<string, string> = {}) {
  return useQuery({
    queryKey: ['favorites', query],
    queryFn: () => apiClient.listFavorites(query),
  });
}

export function useFavoriteStatus(itemId: string | undefined) {
  return useQuery({
    queryKey: ['favorites', 'status', itemId],
    queryFn: () => apiClient.getFavoriteStatus(itemId!),
    enabled: !!itemId,
  });
}

export function useToggleFavorite() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ itemId, isFavorited }: { itemId: string; isFavorited: boolean }) => {
      if (isFavorited) {
        await apiClient.removeFavorite(itemId);
      } else {
        await apiClient.addFavorite(itemId);
      }
    },
    onMutate: async ({ itemId, isFavorited }) => {
      await queryClient.cancelQueries({ queryKey: ['favorites', 'status', itemId] });
      const prev = queryClient.getQueryData(['favorites', 'status', itemId]);
      queryClient.setQueryData(['favorites', 'status', itemId], (old: any) => ({
        isFavorited: !isFavorited,
        count: old ? old.count + (isFavorited ? -1 : 1) : isFavorited ? 0 : 1,
      }));
      return { prev, itemId };
    },
    onError: (_err, _vars, context) => {
      if (context?.prev !== undefined) {
        queryClient.setQueryData(['favorites', 'status', context.itemId], context.prev);
      }
    },
    onSettled: (_data, _err, vars) => {
      queryClient.invalidateQueries({ queryKey: ['favorites', 'status', vars.itemId] });
      queryClient.invalidateQueries({ queryKey: ['favorites'] });
    },
  });
}
